import { FilterToolbar, PageCanvas } from "@/components/page-canvas";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  RecordDescription,
  RecordMetadata,
  RecordRow,
  RecordTitle,
} from "@/components/record-link";
import {
  locationJobsPath,
  locationJobPath,
  newLocationJobPath,
} from "@/routes";
import { dateTime, type Job } from "./types";

export default function Jobs({
  jobs,
  pagination,
  query,
  advance_overdue,
  stage,
}: {
  jobs: Job[];
  advance_overdue: boolean;
  stage: string | null;
  query: string;
  pagination: { page: number; pages: number };
}) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const id = auth.location.id;
  return (
    <AppLayout
      breadcrumbs={[{ title: t("jobs.title"), href: locationJobsPath(id) }]}
    >
      <Head title={t("jobs.title")} />
      <PageCanvas>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-page font-display">{t("jobs.title")}</h1>
          <Button
            render={
              <Link
                href={newLocationJobPath(id, {
                  composition: {
                    origin: "jobs",
                    q: query || undefined,
                    page:
                      pagination.page > 1 ? String(pagination.page) : undefined,
                    advance_overdue: advance_overdue ? "1" : undefined,
                    stage: stage || undefined,
                  },
                })}
              />
            }
          >
            {t("jobs.create")}
          </Button>
        </div>
        <FilterToolbar
          action={locationJobsPath(id)}
          method="get"
          columns="search"
        >
          {stage && <input type="hidden" name="stage" value={stage} />}
          {advance_overdue && (
            <input type="hidden" name="advance_overdue" value="1" />
          )}
          <div className="flex min-w-0 flex-col gap-2">
            <Label htmlFor="job-search">{t("job_board.search")}</Label>
            <Input
              id="job-search"
              name="q"
              defaultValue={query}
              maxLength={200}
            />
          </div>
          <Button type="submit">{t("job_board.search_button")}</Button>
        </FilterToolbar>
        {stage && (
          <p role="status">
            {t("home_attention.filtered", {
              stage: t(`job_board.stages.${stage}`),
            })}{" "}
            <Link className="underline" href={locationJobsPath(id)}>
              {t("payments.all_jobs")}
            </Link>
          </p>
        )}
        {advance_overdue && (
          <p role="status">
            {t("payments.overdue")}{" "}
            <Link className="underline" href={locationJobsPath(id)}>
              {t("payments.all_jobs")}
            </Link>
          </p>
        )}
        <p className="text-muted-foreground text-sm">{t("jobs.entry_help")}</p>
        {!jobs.length && <p>{t("jobs.empty")}</p>}
        {jobs.map((job) => (
          <Card key={job.id} className="py-0">
            <CardContent className="p-0">
              <RecordRow
                className="rounded-lg px-6 py-5"
                href={locationJobPath(id, job.id)}
              >
                <RecordTitle>
                  {job.representation.vehicle.make}{" "}
                  {job.representation.vehicle.model}
                  {job.representation.vehicle.registration_number
                    ? ` · ${job.representation.vehicle.registration_number}`
                    : ""}
                </RecordTitle>
                <RecordDescription className="mt-1">
                  {job.representation.customer.name}
                </RecordDescription>
                <div className="mt-3 space-y-1">
                  <RecordMetadata>
                    {t(`job_board.stages.${job.work_state}`)}
                  </RecordMetadata>
                  <RecordMetadata>
                    {job.bay_name} ·{" "}
                    {dateTime(job.starts_at, job.time_zone, i18n.locale)}
                  </RecordMetadata>
                </div>
                {job.advance_overdue && (
                  <RecordDescription
                    role="alert"
                    className="mt-2 font-medium text-destructive"
                  >
                    {t("payments.overdue")}
                  </RecordDescription>
                )}
              </RecordRow>
            </CardContent>
          </Card>
        ))}
        <nav className="flex gap-4" aria-label={t("jobs.title")}>
          {pagination.page > 1 && (
            <Link
              className="underline"
              href={locationJobsPath(id, {
                page: pagination.page - 1,
                q: query,
                advance_overdue: advance_overdue ? "1" : undefined,
                stage: stage || undefined,
              })}
            >
              {t("customers.previous")}
            </Link>
          )}
          {pagination.page < pagination.pages && (
            <Link
              className="underline"
              href={locationJobsPath(id, {
                page: pagination.page + 1,
                q: query,
                advance_overdue: advance_overdue ? "1" : undefined,
                stage: stage || undefined,
              })}
            >
              {t("customers.next")}
            </Link>
          )}
        </nav>
      </PageCanvas>
    </AppLayout>
  );
}
